import { useEffect, useState } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';

import { aqlQuery } from '#queries/aqlQuery';

import type { LinhaDoMes } from './mesFinanceiro';

export type RetornoHistorico = {
  linhas: LinhaDoMes[];
  /** `YYYY-MM`, do mais antigo ao mais novo, incluindo `mesFinal`. */
  meses: string[];
  carregando: boolean;
  erro: Error | null;
};

/**
 * Traz de uma vez as transações de uma janela de N meses.
 *
 * ⚠️ NÃO recebe `escopo` de propósito: as linhas cruas são as mesmas para
 * pessoal e empresa — quem filtra são os motores (`montarSerie`,
 * `calcularMedias`). Consultar de novo a cada toque no seletor
 * pessoal/empresa seria uma ida ao banco para um dado que já está na mão.
 *
 * ⚠️ Uma consulta só para a janela inteira, nunca N consultas de um mês.
 * Doze meses = doze idas ao banco a cada render inicial da tela Médias.
 *
 * ⚠️ `erro` nunca vira lista vazia silenciosa: uma falha de leitura que
 * parecesse "nenhum gasto no período" mostraria médias ZERADAS com cara de
 * verdade. Mesma lição de `useMesFinanceiro` (ver o guard `doMes.erro === null`
 * em `Inicio.tsx`).
 */
export function useHistorico(
  mesFinal: string,
  quantosMeses: number,
): RetornoHistorico {
  const [linhas, setLinhas] = useState<LinhaDoMes[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<Error | null>(null);

  const mesInicial = monthUtils.subMonths(mesFinal, quantosMeses - 1);
  const meses = monthUtils.rangeInclusive(mesInicial, mesFinal);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setErro(null);

    const inicio = monthUtils.firstDayOfMonth(mesInicial);
    const fim = monthUtils.lastDayOfMonth(mesFinal);

    const query = q('transactions')
      .filter({ date: { $gte: inicio }, $and: [{ date: { $lte: fim } }] })
      .select([
        'id',
        'date',
        'amount',
        'notes',
        'transfer_id',
        'schedule',
        'category',
        'is_parent',
        'is_child',
        'starting_balance_flag',
        { contaForaDoOrcamento: 'account.offbudget' },
      ]);

    aqlQuery(query)
      .then(({ data }) => {
        if (cancelado) return;

        const brutas = (data ?? []) as Array<Record<string, unknown>>;
        setLinhas(
          brutas.map(t => ({
            id: String(t.id),
            date: String(t.date),
            amount: Number(t.amount ?? 0),
            notes: (t.notes as string | null) ?? null,
            transfer_id: (t.transfer_id as string | null) ?? null,
            schedule: (t.schedule as string | null) ?? null,
            category: (t.category as string | null) ?? null,
            contaForaDoOrcamento: Boolean(t.contaForaDoOrcamento),
            ehSaldoInicial: Boolean(t.starting_balance_flag),
            ehPai: Boolean(t.is_parent),
            ehFilho: Boolean(t.is_child),
          })),
        );
        setCarregando(false);
      })
      .catch((e: unknown) => {
        if (cancelado) return;
        setLinhas([]);
        setErro(e instanceof Error ? e : new Error(String(e)));
        setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [mesInicial, mesFinal]);

  return { linhas, meses, carregando, erro };
}
